/**
 * Venue slots (V55 / F1). The dashboard keeps three NAMED slots on every
 * two-sided container (`kalshi`, `polymarketUs`, `polymarket`) rather than a
 * venue-keyed map — the venue count is three and staying three, and named
 * slots keep every existing `.polymarket` reader compiling. This module is the
 * one place that knows the slot names and the display order, so new code
 * reads through it instead of growing another `venue === 'kalshi' ? … : …`
 * ternary; old sites migrate opportunistically.
 */
import type { Venue } from '@/types';
import type { VenueBook } from '@/views/ValueDashboard/types';

/** Display order everywhere the venues sit side by side: Kalshi, then
 *  Polymarket US, then intl Polymarket (Josh, 2026-09-16: "right in between"). */
export const VENUE_ORDER: readonly Venue[] = ['kalshi', 'polymarket_us', 'polymarket'];

export interface VenuePair {
  away: VenueBook;
  home: VenueBook;
}

/** Any container with the three optional away/home slots (GameRow and its
 *  synthesized siblings). */
export interface VenueSlots {
  kalshi?: VenuePair | null;
  polymarket?: VenuePair | null;
  polymarketUs?: VenuePair | null;
}

export function venueSlot(game: VenueSlots, venue: Venue): VenuePair | null {
  switch (venue) {
    case 'kalshi':
      return game.kalshi ?? null;
    case 'polymarket_us':
      return game.polymarketUs ?? null;
    case 'polymarket':
      return game.polymarket ?? null;
  }
}

/** All three slots keyed by venue, in one read. Absent slots are null. */
export function venueBooks(game: VenueSlots): Record<Venue, VenuePair | null> {
  return {
    kalshi: game.kalshi ?? null,
    polymarket_us: game.polymarketUs ?? null,
    polymarket: game.polymarket ?? null,
  };
}

/** Both Polymarket venues share the single-binary-per-game shape (one
 *  instrument projected onto away/home, shared volume + liquidity cue). */
export function isPolyFamily(venue: Venue): boolean {
  return venue === 'polymarket' || venue === 'polymarket_us';
}

/** The venues some row on this board actually carries, in display order. A
 *  venue row filter keyed on a venue no row offers (a persisted "US markets
 *  only" on an NHL board where node1 forwards no Polymarket US rows) would hide
 *  every matchup with no chip drawn to clear it — the dashboard treats such a
 *  filter as inert until the board offers the venue (2026-10-08). */
export function offeredVenues(games: readonly VenueSlots[]): ReadonlySet<Venue> {
  return new Set(VENUE_ORDER.filter((venue) => games.some((game) => venueSlot(game, venue) != null)));
}

export function isVenue(value: unknown): value is Venue {
  return value === 'kalshi' || value === 'polymarket' || value === 'polymarket_us';
}
